import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useMemberEligibility,
} from '@sentara-health/sentara-api-hooks-core';
import { errorhandler, getDecryptedValue, UserDetails } from 'common';
import {
  Headings,
  Loader,
  Table,
  Paragraph,
} from '@sentara-health/optimahealth_web';
import { GlobalContext } from 'context';
import { transformBenefitData } from '../utils/BenefitDataTransformer';

const Benefits = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<any>(false);
  const [rawBenefitData, setRawBenefitData] = useState<any>();
  const { loginMemberId } = useContext(GlobalContext);
  const { MemberEligibilityRequest } = useMemberEligibility();
  const hasCalledAPI = useRef(false);

  const eligibilityPlans = async () => {
    const bearerToken = await getDecryptedValue(UserDetails.bearerToken);

    const EligibilityrequestBody = {
      identifier: [
        {
          system: 'memberId',
          value: loginMemberId,
        },
      ],
    };

    const eligibilityResponse = await MemberEligibilityRequest(
      bearerToken,
      EligibilityrequestBody
    );

    // Store the raw benefit data
    if (eligibilityResponse?.data?.eligibilityPlans) {
      const filteredPlan = eligibilityResponse.data.eligibilityPlans.find(
        (plan: any) =>
          plan.extension?.inOutNetwork === 'Y' &&
          plan.extension?.coverageLevel === 'IND' &&
          plan.identifier?.some(
            (id: any) => id.system === 'MemberId' && id.value === loginMemberId
          )
      );
      if (filteredPlan) {
        const benefitData = filteredPlan.items;
        setRawBenefitData(benefitData);
      }
    } else {
      errorhandler(eligibilityResponse?.errorCode);
    }
    setLoading(false);
  };

  // Transform data whenever language changes
  const generalVisit = useMemo(() => {
    if (rawBenefitData) {
      return transformBenefitData(rawBenefitData);
    }
    return null;
  }, [rawBenefitData, t]);

  useEffect(() => {
    if (!hasCalledAPI.current) {
      hasCalledAPI.current = true;
      const asyncBenefitsCall = async () => {
        setLoading(true);
        if (loginMemberId) {
          await eligibilityPlans();
        } else {
          hasCalledAPI.current = false;
          setLoading(false);
        }
      };
      asyncBenefitsCall();
    }
  }, [loginMemberId]);

  interface BenefitItem {
    key: string;
    displayTitle: string;
    value: string;
  }

  const BenefitsValue = (generalVisit: any) => {
    return generalVisit?.map((item: BenefitItem) => (
      <tr key={`${item.key}`}>
        <th
          scope="col"
          data-testid={`${item.key.charAt(0).toLowerCase() + item.key.slice(1)
            }`.replace(/\s/g, '')}
        >
          {t(item.displayTitle)}
        </th>
        <td
          data-testid={`${item.key.charAt(0).toLowerCase() + item.key.slice(1)
            }Value`.replace(/\s/g, '')}
        >
          {item.value}
        </td>
      </tr>
    ));
  };

  const generalVisitsHeader = [{ text: t('benefits.generalVisits'), colspan: 2 }];
  const generalVisitsRowsChildren = BenefitsValue(generalVisit?.generalVisits);

  const discountedNetworkHeader = [
    { text: t('benefits.discountedNetwork'), colspan: 2 },
  ];
  const discountedNetworkRowsChildren = BenefitsValue(generalVisit?.discountedNetwork);

  return (
    <>
      {loading && <Loader />}
      <div>
        <div className="mb-4">
          <Headings
            level={1}
            text={t('common.benefits')}
            variant="semiBold"
            dataTestId="benefitsHeader"
          />
        </div>
        {!generalVisit && !loading && (
          <Paragraph text={t('common.noRecords')} variant="regular" />
        )}
        {generalVisit?.generalVisits &&
          generalVisit?.generalVisits?.length > 0 ? (
          <div className="benefits-table table-responsive overflow-auto">
            <Table
              header={generalVisitsHeader}
              rowsChildren={generalVisitsRowsChildren}
            />
          </div>
        ) : null}
        {generalVisit?.discountedNetwork &&
          generalVisit?.discountedNetwork?.length > 0 ? (
          <div className="benefits-table table-responsive overflow-auto">
            <Table
              header={discountedNetworkHeader}
              rowsChildren={discountedNetworkRowsChildren}
            />
          </div>
        ) : null}
      </div>
    </>
  );
};

export default Benefits;